/**
 * Per-cell notes — "why is this VAT201 period still outstanding".
 *
 * Confirmed 2026-08-26 as what the matrix needs, matching SmartPractice's own
 * per-cell notes icon. A panel rather than the Flask build's separate page:
 * the matrix is a scanning view, and losing your scroll position and filters to
 * read one note was a cost of that build's page-per-route shape, not a
 * decision. Everything else about it is unchanged, including that notes belong
 * to one period rather than to the client in general.
 *
 * Who wrote a note depends on whether the server has authentication configured.
 * Signed in, the author is the signed-in user and the field disappears -- the
 * server ignores anything sent for it. In anonymous mode there is nobody to
 * attribute to, so the field stays and asks, which is what this did for all of
 * Phases 1-4 (docs/open_questions.md #23).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { api, type AuthState, type NotesResponse } from '../api'
import { ErrorState, LoadingState } from './States'

export function NotesPanel({
  obligationPeriodId,
  auth,
  onClose,
  onAdded,
}: {
  obligationPeriodId: string
  auth: AuthState
  onClose: () => void
  onAdded: () => void
}) {
  const signedInAs = auth.user?.name ?? null
  const [data, setData] = useState<NotesResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [author, setAuthor] = useState('')
  const authorRequired = signedInAs === null
  const [body, setBody] = useState('')
  const [saving, setSaving] = useState(false)
  const authorRef = useRef<HTMLInputElement>(null)
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      setData(await api.notes(obligationPeriodId))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [obligationPeriodId])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    // Focus whichever field the person actually has to fill in.
    ;(authorRequired ? authorRef.current : bodyRef.current)?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if ((authorRequired && author.trim() === '') || body.trim() === '') return
    setSaving(true)
    try {
      // The server ignores this when someone is signed in; it is sent anyway so
      // there is one call, not two.
      await api.addNote(obligationPeriodId, signedInAs ?? author.trim(), body.trim())
      setBody('')
      await load()
      // The matrix shows a count badge per cell, so it has to hear about this.
      onAdded()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="flex-1 bg-[rgba(11,31,58,.28)]"
        onClick={onClose}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Notes for this period"
        className="flex w-full max-w-[30rem] flex-col bg-white shadow-eq-raised"
      >
        <header className="border-b border-eq-border px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="font-heading text-[11px] font-bold uppercase tracking-[.08em] text-eq-neutral">
                Notes
              </div>
              <h2 className="mt-1 truncate text-[19px]">{data?.clientName ?? '…'}</h2>
              {data && (
                <div className="mt-1 text-[12.5px] text-eq-helper">
                  {data.itemCode} · {formatDate(data.periodStart)} to {formatDate(data.periodEnd)}
                </div>
              )}
            </div>
            <button type="button" className="eq-btn-tertiary py-2" onClick={onClose}>
              Close
            </button>
          </div>
          <p className="mt-3 text-[12.5px] text-eq-helper">
            These notes belong to this period only, not to the client in general.
          </p>
        </header>

        <form onSubmit={submit} className="border-b border-eq-border px-6 py-5">
          {authorRequired ? (
            <label className="block text-[12.5px] text-eq-neutral">
              Staff member
              <input
                ref={authorRef}
                className="eq-input mt-1 w-full"
                value={author}
                onChange={(event) => setAuthor(event.target.value)}
                placeholder="Your name"
                required
              />
            </label>
          ) : (
            <p className="text-[12.5px] text-eq-helper">
              Writing as <strong className="text-eq-charcoal">{signedInAs}</strong>
            </p>
          )}
          <label className="mt-3 block text-[12.5px] text-eq-neutral">
            Note
            <textarea
              ref={bodyRef}
              className="eq-input mt-1 min-h-[5rem] w-full resize-y"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="What is holding this one up?"
              required
            />
          </label>
          <button
            type="submit"
            className="eq-btn-primary mt-3"
            disabled={saving || (authorRequired && author.trim() === '') || body.trim() === ''}
          >
            {saving ? 'Adding…' : 'Add note'}
          </button>
        </form>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && <ErrorState message={error} />}
          {!error && !data && <LoadingState what="notes" />}
          {data && data.notes.length === 0 && (
            <p className="text-[14px] text-eq-neutral">No notes yet for this period.</p>
          )}
          <ul className="space-y-3">
            {data?.notes.map((note) => (
              <li key={note.id} className="rounded-eq-md border border-eq-border px-4 py-3">
                <div className="text-[12.5px] text-eq-helper">
                  <strong className="text-eq-charcoal">{note.author}</strong>
                  {' · '}
                  {formatTimestamp(note.createdAt)}
                </div>
                <p className="mt-1 whitespace-pre-wrap text-[14px]">{note.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </aside>
    </div>
  )
}

/** A @db.Date arrives as an ISO instant; only its calendar date is meaningful. */
const formatDate = (iso: string): string => iso.slice(0, 10)

/**
 * `Note.createdAt` is a real instant (timestamptz), so it is shown in
 * Africa/Johannesburg rather than in whatever zone the browser happens to be
 * in — the practice is in one place and a note's time should read the same on
 * every machine.
 */
function formatTimestamp(iso: string): string {
  return new Intl.DateTimeFormat('en-ZA', {
    timeZone: 'Africa/Johannesburg',
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso))
}
